import React, { useRef, memo, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, InstagramIcon, LinkedinIcon, MailIcon, PlusIcon, CheckIcon, MessageSquareIcon, GitBranchIcon, UsersIcon, LightbulbIcon, WrenchIcon, SparklesIcon, ZapIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9pYpmBqnTFtudjrgYsyKLh/METRO-BRANDBOOK_Mesa_de_trabajo_1_copia_11.svg';
/* ------------------------------------------------------------------ */
/*  Shared mockup primitives                                           */
/* ------------------------------------------------------------------ */
function BrowserFrame({
  url,
  children
}: {
  url: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-md overflow-hidden border border-[var(--divider)] shadow-lg bg-white" data-id="element-1621">
      <div className="bg-[#F4F5F7] border-b border-[var(--divider)] px-3 py-2 flex items-center gap-3" data-id="element-1622">
        <div className="flex gap-1.5" data-id="element-1623">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" data-id="element-1624" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" data-id="element-1625" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" data-id="element-1626" />
        </div>
        <div className="flex-1 bg-white rounded-sm border border-[var(--divider)] px-2.5 py-0.5 text-[10px] font-mono text-[var(--text-muted)] truncate" data-id="element-1627">
          {url}
        </div>
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-1628">
          v2026.1
        </div>
      </div>
      <div className="bg-white" data-id="element-1629">{children}</div>
    </div>;
}
function SiteNav({
  active
}: {
  active: 'inicio' | 'servicios' | 'packs' | 'ai' | 'nosotros' | 'terms';
}) {
  const items: Array<{
    key: typeof active;
    label: string;
  }> = [{
    key: 'inicio',
    label: 'Inicio'
  }, {
    key: 'servicios',
    label: 'Servicios'
  }, {
    key: 'packs',
    label: 'Packs'
  }, {
    key: 'ai',
    label: 'AI'
  }, {
    key: 'nosotros',
    label: 'Nosotros'
  }];
  return <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--divider)]" data-id="element-1630">
      <div className="flex items-center gap-6" data-id="element-1631">
        <img src={METRO_LOGO_URL} alt="me—tro" className="h-5 w-auto" data-id="element-1632" />
        <nav className="flex items-center gap-5 text-[10px]" data-id="element-1633">
          {items.map(i => <a key={i.key} className={i.key === active ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]'} data-id="element-1634">
              {i.label}
            </a>)}
          <a className="text-[var(--text-muted)]" data-id="element-1635">Contacto</a>
        </nav>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-1636">
        <span data-id="element-1637">ES / EN</span>
        <button className="px-3 py-1.5 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium" data-id="element-1638">
          Hablemos
        </button>
      </div>
    </div>;
}
function SiteFooter() {
  return <div className="bg-[var(--text-ink)] text-white px-6 py-8 mt-0" data-id="element-1639">
      <div className="grid grid-cols-12 gap-4 mb-6" data-id="element-1640">
        <div className="col-span-6" data-id="element-1641">
          <div className="font-space text-2xl font-bold tracking-tight leading-tight" data-id="element-1642">
            Donde las
            <br data-id="element-1643" />
            ideas conectan.
          </div>
          <div className="text-[10px] opacity-60 mt-2" data-id="element-1644">
            Metro Agency · Barcelona
          </div>
        </div>
        <div className="col-span-2" data-id="element-1645">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1646">
            EXPLORA
          </div>
          <div className="space-y-1 text-[10px]" data-id="element-1647">
            <div data-id="element-1648">Inicio</div>
            <div data-id="element-1649">Servicios</div>
            <div data-id="element-1650">Packs</div>
            <div data-id="element-1651">AI</div>
            <div data-id="element-1652">Nosotros</div>
            <div data-id="element-1653">Contacto</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-1654">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1655">
            ESTUDIO
          </div>
          <div className="space-y-1 text-[10px] opacity-80" data-id="element-1656">
            <div data-id="element-1657">Carrer de la Marina, 145</div>
            <div data-id="element-1658">08013 Barcelona</div>
            <div data-id="element-1659">hello@metroagency.co</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-1660">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1661">
            SOCIAL
          </div>
          <div className="flex gap-2 mt-1" data-id="element-1662">
            <InstagramIcon className="w-3.5 h-3.5" data-id="element-1663" />
            <LinkedinIcon className="w-3.5 h-3.5" data-id="element-1664" />
            <MailIcon className="w-3.5 h-3.5" data-id="element-1665" />
          </div>
        </div>
      </div>
      <div className="border-t border-white/20 pt-3 flex items-center justify-between text-[9px] opacity-60" data-id="element-1666">
        <span data-id="element-1667">© 2026 Metro Agency S.L. · NIF B12345678</span>
        <span data-id="element-1668">Política de privacidad · Aviso legal · Cookies</span>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  Service network mark — small reusable graphic                      */
/* ------------------------------------------------------------------ */
function ServiceNetwork({
  height = 200
}: {
  height?: number;
}) {
  return <svg viewBox="0 0 600 200" className="w-full" style={{
    height
  }} data-id="element-1669">
      <line x1="20" y1="60" x2="580" y2="60" stroke="#1A2BFF" strokeWidth="3" data-id="element-1670" />
      <line x1="20" y1="100" x2="580" y2="100" stroke="#E8C547" strokeWidth="3" data-id="element-1671" />
      <line x1="20" y1="140" x2="580" y2="140" stroke="#F26A35" strokeWidth="3" data-id="element-1672" />
      <line x1="20" y1="180" x2="580" y2="180" stroke="#2A8E47" strokeWidth="3" data-id="element-1673" />
      {[60, 100, 140, 180].map((y, i) => [120, 240, 360, 480].map((x, j) => <circle key={`${i}-${j}`} cx={x} cy={y} r="6" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="2" data-id="element-1674" />))}
      <text x="20" y="30" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#6B6E78" letterSpacing="1.5" data-id="element-1675">
        STRATEGY · CREATIVE · PRODUCTION · PERFORMANCE
      </text>
    </svg>;
}
/* ================================================================== */
/*  HOME PAGE MOCKUP                                                   */
/* ================================================================== */
function HomePageMockup() {
  return <BrowserFrame url="metroagency.co" data-id="element-1676">
      <SiteNav active="inicio" data-id="element-1677" />

      {/* Hero — full bleed cool grey */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1678">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1679">
          AGENCIA CREATIVA · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1680">
          Donde las
          <br data-id="element-1681" />
          ideas conectan.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1682">
          Conectamos estrategia, creatividad y producción en una sola línea —
          para que las campañas lleguen donde tienen que llegar, sin perderse
          entre proveedores.
        </div>
        <div className="flex items-center gap-3 mt-6" data-id="element-1683">
          <button className="px-4 py-2 bg-[var(--text-ink)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1684">
            Build something that moves people
            <ArrowRightIcon className="w-3 h-3" data-id="element-1685" />
          </button>
          <button className="px-4 py-2 border border-[var(--text-ink)] rounded-full text-xs font-medium" data-id="element-1686">
            Ver trabajos
          </button>
        </div>
        <div className="mt-10" data-id="element-1687">
          <ServiceNetwork height={180} data-id="element-1688" />
        </div>
      </div>

      {/* Service network explainer */}
      <div className="px-6 py-10 bg-white" data-id="element-1689">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1690">
          01 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight max-w-lg mb-6" data-id="element-1691">
          Cuatro líneas. Una red. Cero traspasos.
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-1692">
          {[{
          n: '01',
          t: 'Estrategia',
          d: 'Posicionamiento, audiencias, mensaje. Donde decidimos qué construir y por qué.'
        }, {
          n: '02',
          t: 'Creatividad',
          d: 'Identidad, dirección de arte, narrativa. La idea con la que el resto se conecta.'
        }, {
          n: '03',
          t: 'Producción',
          d: 'Vídeo, fotografía, web, eventos. Donde la idea se vuelve real.'
        }, {
          n: '04',
          t: 'Performance',
          d: 'Paid media, analítica, optimización. Donde medimos si la conexión funcionó.'
        }].map(c => <div key={c.n} className="border border-[var(--divider)] p-4 rounded-sm" data-id="element-1693">
              <div className="font-mono text-[10px] text-[var(--accent-blue)] font-bold mb-2" data-id="element-1694">
                {c.n}
              </div>
              <div className="font-space font-bold text-base mb-1" data-id="element-1695">{c.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1696">
                {c.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* Manifesto callout */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-1697">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-1698">
          MANIFIESTO
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-1699">
          No creemos en magia. Creemos en sistemas que funcionan. Construimos
          infraestructura para ideas que duran — campañas que conectan
          estrategia con ejecución, marcas que mueven personas.
        </div>
        <div className="mt-5 text-[10px] opacity-70 font-mono tracking-widest" data-id="element-1700">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Selected work */}
      <div className="px-6 py-10 bg-white" data-id="element-1701">
        <div className="flex items-end justify-between mb-5" data-id="element-1702">
          <div data-id="element-1703">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1704">
              02 · TRABAJO SELECCIONADO
            </div>
            <div className="font-space font-bold text-2xl tracking-tight" data-id="element-1705">
              Lo último de la línea.
            </div>
          </div>
          <a className="text-[10px] font-medium underline" data-id="element-1706">Ver todo →</a>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-1707">
          {[{
          c: '#1A2BFF',
          t: 'Cliente · Sector',
          n: 'Campaña 360'
        }, {
          c: '#0E0E12',
          t: 'Cliente · Sector',
          n: 'Identidad'
        }, {
          c: '#E8C547',
          t: 'Cliente · Sector',
          n: 'Producción'
        }].map((w, i) => <div key={i} data-id="element-1708">
              <div className="aspect-[4/5] rounded-sm flex items-end p-3" style={{
            background: w.c
          }} data-id="element-1709">
                <div className={'text-[10px] font-bold ' + (w.c === '#E8C547' ? 'text-[var(--text-ink)]' : 'text-white')} data-id="element-1710">
                  {w.n}
                </div>
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-1.5 font-mono tracking-wider" data-id="element-1711">
                {w.t}
              </div>
            </div>)}
        </div>
      </div>

      {/* Clients strip */}
      <div className="px-6 py-6" style={{
      background: '#EDEEF1'
    }} data-id="element-1712">
        <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-3 text-center" data-id="element-1713">
          CONFÍAN EN NOSOTROS
        </div>
        <div className="flex items-center justify-around opacity-50" data-id="element-1714">
          {['CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE'].map((c, i) => <div key={i} className="text-[10px] font-bold tracking-wider" data-id="element-1715">
                {c}
              </div>)}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-1716">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-1717">
          ¿Una idea que conectar?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-1718">
          Cuéntanos el proyecto, el contexto y el plazo. Respondemos en menos de
          un día laborable.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1719">
          Start the conversation
          <ArrowRightIcon className="w-3 h-3" data-id="element-1720" />
        </button>
      </div>

      <SiteFooter data-id="element-1721" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  SERVICES PAGE MOCKUP                                               */
/* ================================================================== */
function ServicesPageMockup() {
  return <BrowserFrame url="metroagency.co/servicios" data-id="element-1722">
      <SiteNav active="servicios" data-id="element-1723" />

      {/* Hero — full bleed blue */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-1724">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-1725">
          SERVICIOS
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1726">
          Una línea.
          <br data-id="element-1727" />
          Cuatro estaciones.
        </div>
        <div className="font-helvetica text-sm opacity-90 leading-relaxed max-w-lg" data-id="element-1728">
          Estrategia, creatividad, producción y performance — integradas de
          principio a fin. Sin externalizar las partes críticas. Sin perder la
          coherencia entre fases.
        </div>
        <div className="mt-8 grid grid-cols-4 gap-2 max-w-2xl" data-id="element-1729">
          {['Estrategia', 'Creatividad', 'Producción', 'Performance'].map((s, i) => <div key={i} className="text-center text-[9px] py-2 rounded-sm border border-white/30" data-id="element-1730">
                <div className="font-mono opacity-70" data-id="element-1731">0{i + 1}</div>
                <div className="font-bold mt-0.5" data-id="element-1732">{s}</div>
              </div>)}
        </div>
      </div>

      {/* Service blocks */}
      <div className="px-6 py-10 bg-white space-y-4" data-id="element-1733">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-1734">
          01 · QUÉ HACEMOS
        </div>

        {[{
        cat: 'CREATIVIDAD',
        num: '01',
        title: 'Identidad, dirección de arte y narrativa',
        desc: 'Construimos sistemas de marca que viajan con todo lo demás. No logos sueltos — sistemas que aguantan el uso real.',
        items: ['Identidad visual y verbal', 'Dirección de arte para campaña', 'Sistemas de diseño', 'Brand guidelines y handover']
      }, {
        cat: 'MARKETING & PERFORMANCE',
        num: '02',
        title: 'Estrategia, datos y optimización',
        desc: 'Estrategia con cabeza de negocio. Paid media, analítica y optimización con foco en el resultado, no en el dashboard.',
        items: ['Estrategia de marca y go-to-market', 'Paid social y search', 'Analítica y atribución', 'CRO y optimización continua']
      }, {
        cat: 'PRODUCCIÓN & EVENTOS',
        num: '03',
        title: 'Vídeo, fotografía, web y experiencias',
        desc: 'Producción in-house de principio a fin. Lo que diseñamos lo producimos — sin traspasos, sin teléfono escacharrado.',
        items: ['Vídeo y fotografía', 'Diseño y desarrollo web', 'Eventos y activaciones', 'Contenido para social y paid']
      }].map(s => <div key={s.num} className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-1735">
            <div className="flex items-start gap-4 p-5 border-b border-[var(--divider)]" data-id="element-1736">
              <div className="flex-shrink-0" data-id="element-1737">
                <div className="font-mono text-[10px] text-[var(--accent-blue)] tracking-widest mb-1 font-bold" data-id="element-1738">
                  {s.cat}
                </div>
                <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none" data-id="element-1739">
                  {s.num}
                </div>
              </div>
              <div className="flex-1" data-id="element-1740">
                <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-1741">
                  {s.title}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-1742">
                  {s.desc}
                </div>
              </div>
              <MessageSquareIcon className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" strokeWidth={1.5} data-id="element-1743" />
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 px-5 py-3 bg-[var(--bg-canvas)]" data-id="element-1744">
              {s.items.map(it => <div key={it} className="flex items-start gap-2 text-[10px]" data-id="element-1745">
                  <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-1746" />
                  <span data-id="element-1747">{it}</span>
                </div>)}
            </div>
          </div>)}
      </div>

      {/* Approach callout */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1748">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-1749">
          02 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1750">
          Hands-on. End-to-end.
          <br data-id="element-1751" />
          Sin agencias intermedias.
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-1752">
          {[{
          t: 'Un solo equipo',
          d: 'El mismo equipo que diseña es el que produce. Coherencia por construcción.'
        }, {
          t: 'Un solo punto de contacto',
          d: 'Hablas con quien decide. No con quien gestiona al que decide.'
        }, {
          t: 'Un solo lenguaje',
          d: 'Lo estratégico, lo creativo y lo técnico hablan el mismo idioma desde el primer día.'
        }].map(b => <div key={b.t} className="bg-white p-4 rounded-sm border border-[var(--divider)]" data-id="element-1753">
              <div className="font-space font-bold text-sm mb-1.5" data-id="element-1754">{b.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1755">
                {b.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-1756">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-1757">
          ¿Planificando una campaña?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-1758">
          Mejor empezar por el mapa. Cuéntanos el objetivo y dibujamos la línea
          contigo.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1759">
          Map your next campaign
          <ArrowRightIcon className="w-3 h-3" data-id="element-1760" />
        </button>
      </div>

      <SiteFooter data-id="element-1761" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  ABOUT PAGE MOCKUP                                                  */
/* ================================================================== */
function AboutPageMockup() {
  return <BrowserFrame url="metroagency.co/nosotros" data-id="element-1762">
      <SiteNav active="nosotros" data-id="element-1763" />

      {/* Hero */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1764">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1765">
          NOSOTROS · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1766">
          Construimos
          <br data-id="element-1767" />
          la infraestructura.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1768">
          Metro nace en Barcelona, de la dificultad de encontrar una agencia
          digital que ejecute la campaña completa sin externalizar las partes
          críticas. Construimos lo que nos hubiera gustado contratar.
        </div>
      </div>

      {/* Dark manifesto */}
      <div className="px-6 py-12 text-white" style={{
      background: '#0E0E12'
    }} data-id="element-1769">
        <div className="font-mono text-[9px] tracking-widest mb-3 text-[var(--accent-blue)]" data-id="element-1770">
          MANIFIESTO · 80 PALABRAS
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-1771">
          <p className="mb-3 font-bold" data-id="element-1772">
            No creemos en magia. Creemos en sistemas que funcionan.
          </p>
          <p className="mb-3 text-base opacity-90 font-normal" data-id="element-1773">
            En un mundo saturado de contenido, construimos infraestructura para
            ideas que duran. Campañas que conectan estrategia con ejecución,
            marcas que mueven personas, historias que merecen la atención.
          </p>
          <p className="text-base opacity-90 font-normal" data-id="element-1774">
            No perseguimos trends. Construimos líneas entre lo que necesitas
            comunicar y quién necesita escucharlo.
          </p>
        </div>
        <div className="mt-6 pt-4 border-t border-white/20 font-mono text-[10px] tracking-widest opacity-70" data-id="element-1775">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Voice */}
      <div className="px-6 py-10 bg-white" data-id="element-1776">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1777">
          01 · CÓMO HABLAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1778">
          Punto. No exclamación.
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-1779">
          <div className="p-4 rounded-sm text-white" style={{
          background: '#1A2BFF'
        }} data-id="element-1780">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-1781">
              USAMOS
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-1782">
              Conectar · construir · red · línea · estrategia · campaña · real ·
              honesto · directo · intencional
            </div>
          </div>
          <div className="p-4 rounded-sm bg-[var(--text-ink)] text-white" data-id="element-1783">
            <div className="font-mono text-[9px] opacity-60 tracking-widest mb-2" data-id="element-1784">
              NUNCA
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-1785">
              Magia · wow factor · rock · synergy · disrupt · journey ·
              game-changer · guru · ninja
            </div>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1786">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-1787">
          02 · EQUIPO
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1788">
          Las personas conducen el tren.
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-1789">
          {[['Nombre Apellido', 'Strategy & accounts'], ['Nombre Apellido', 'Creative director'], ['Nombre Apellido', 'Production lead'], ['Nombre Apellido', 'Performance & data']].map(([n, r], i) => <div key={i} data-id="element-1790">
              <div className="aspect-square rounded-sm mb-2" style={{
            background: i % 2 === 0 ? '#1A2BFF' : '#0E0E12'
          }} data-id="element-1791" />
              <div className="font-space font-bold text-[11px] leading-tight" data-id="element-1792">
                {n}
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-0.5" data-id="element-1793">
                {r}
              </div>
            </div>)}
        </div>
      </div>

      {/* Numbers */}
      <div className="px-6 py-10 bg-white" data-id="element-1794">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-5" data-id="element-1795">
          03 · ESTACIONES POR LAS QUE HEMOS PASADO
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-1796">
          {[['+50', 'Campañas integradas'], ['+30', 'Marcas con identidad nueva'], ['12', 'Sectores cubiertos'], ['4', 'Idiomas operativos']].map(([n, l]) => <div key={l} className="border-t border-[var(--text-ink)] pt-3" data-id="element-1797">
              <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none mb-1" data-id="element-1798">
                {n}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-tight" data-id="element-1799">
                {l}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12" style={{
      background: '#1A2BFF'
    }} data-id="element-1800">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4 text-white" data-id="element-1801">
          Empezamos por una conversación.
        </div>
        <div className="text-sm text-white/80 mb-5 max-w-md" data-id="element-1802">
          Sin briefing pre-rellenado. Sin formulario de 12 campos. Solo
          cuéntanos el contexto.
        </div>
        <button className="px-5 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1803">
          Let's connect the dots
          <ArrowRightIcon className="w-3 h-3" data-id="element-1804" />
        </button>
      </div>

      <SiteFooter data-id="element-1805" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  PACKS PAGE MOCKUP                                                  */
/* ================================================================== */
function PriceTag({
  amount,
  highlight = false,
  small = false
}: {
  amount: string;
  highlight?: boolean;
  small?: boolean;
}) {
  return <div className={'inline-flex items-baseline gap-1 ' + (highlight ? 'text-[var(--accent-blue)]' : '')} data-id="element-1806">
      <span className={'font-space font-bold tracking-tight leading-none ' + (small ? 'text-xl' : 'text-3xl')} data-id="element-1807">
        {amount}
      </span>
      <span className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider" data-id="element-1808">
        IVA INCL.
      </span>
    </div>;
}
function PackCard({
  tier,
  tierLabel,
  name,
  tagline,
  price,
  timeline,
  ideal,
  features,
  warnings,
  cta,
  accent,
  savings,
  combo
}: {
  tier: string;
  tierLabel: string;
  name: string;
  tagline: string;
  price: string;
  timeline: string;
  ideal: string;
  features: string[];
  warnings?: string[];
  cta: string;
  accent: 'blue' | 'ink' | 'grey';
  savings?: string;
  combo?: boolean;
}) {
  const bg = accent === 'blue' ? '#1A2BFF' : accent === 'ink' ? '#0E0E12' : '#EDEEF1';
  const fg = accent === 'grey' ? '#0E0E12' : '#FFFFFF';
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-1809">
      {/* Header */}
      <div className="p-5" style={{
      background: bg,
      color: fg
    }} data-id="element-1810">
        <div className="flex items-center justify-between mb-3" data-id="element-1811">
          <div className="font-mono text-[9px] tracking-widest opacity-75" data-id="element-1812">
            {tier} · {tierLabel}
          </div>
          {combo && <div className="text-[8px] font-bold tracking-widest bg-white/15 px-2 py-0.5 rounded-full" data-id="element-1813">
              COMBO
            </div>}
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-1" data-id="element-1814">
          {name}
        </div>
        <div className="text-[11px] opacity-85 leading-relaxed mb-4" data-id="element-1815">
          {tagline}
        </div>
        <div className="flex items-baseline gap-2" data-id="element-1816">
          <span className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-1817">
            {price}
          </span>
          <span className="text-[9px] font-mono tracking-wider opacity-75" data-id="element-1818">
            IVA INCL.
          </span>
        </div>
        {savings && <div className="text-[10px] mt-1 opacity-90 font-medium" data-id="element-1819">
            {savings}
          </div>}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col" data-id="element-1820">
        <div className="grid grid-cols-2 gap-3 pb-3 border-b border-[var(--divider)] mb-3 text-[10px]" data-id="element-1821">
          <div data-id="element-1822">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1823">
              PLAZO
            </div>
            <div className="font-medium" data-id="element-1824">{timeline}</div>
          </div>
          <div data-id="element-1825">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1826">
              IDEAL PARA
            </div>
            <div className="font-medium leading-snug" data-id="element-1827">{ideal}</div>
          </div>
        </div>

        {warnings && warnings.length > 0 && <div className="mb-3" data-id="element-1828">
            <div className="font-mono text-[8px] text-[var(--accent-orange)] tracking-widest mb-2 font-bold" data-id="element-1829">
              LIMITACIONES EXPLÍCITAS
            </div>
            <ul className="space-y-1.5" data-id="element-1830">
              {warnings.map((w, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug text-[var(--text-muted)]" data-id="element-1831">
                  <span className="text-[var(--accent-orange)] flex-shrink-0" data-id="element-1832">
                    ⚠
                  </span>
                  <span data-id="element-1833">{w}</span>
                </li>)}
            </ul>
          </div>}

        <div data-id="element-1834">
          <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1835">
            INCLUYE
          </div>
          <ul className="space-y-1.5" data-id="element-1836">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug" data-id="element-1837">
                <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-1838" />
                <span data-id="element-1839">{f}</span>
              </li>)}
          </ul>
        </div>

        <div className="mt-5 pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-3" data-id="element-1840">
          <button className="flex-1 px-3 py-2 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-1841">
            {cta}
            <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-1842" />
          </button>
          <button className="px-3 py-2 border border-[var(--text-ink)] rounded-full text-[10px] font-medium" data-id="element-1843">
            Agendar
          </button>
        </div>
      </div>
    </div>;
}
function PacksPageMockup() {
  return <BrowserFrame url="metroagency.co/packs" data-id="element-1844">
      <SiteNav active="packs" data-id="element-1845" />

      {/* Hero */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1846">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1847">
          PACKS · TODOS LOS PRECIOS CON IVA INCLUIDO
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1848">
          Cinco categorías.
          <br data-id="element-1849" />
          Una agencia.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1850">
          Web, marca, contenido, marketing y sistemas completos — todo bajo el
          mismo techo. Packs cerrados con alcance, plazo y precio definidos. Sin
          sorpresas. Sin externalizar lo crítico.
        </div>

        {/* Category strip */}
        <div className="mt-8 grid grid-cols-5 gap-2 max-w-4xl" data-id="element-1851">
          {[{
          n: '01',
          t: 'Web',
          d: 'De Micro a Premium'
        }, {
          n: '02',
          t: 'Brand',
          d: 'Identidad + sistema'
        }, {
          n: '03',
          t: 'Content',
          d: 'Vídeo, foto, copy'
        }, {
          n: '04',
          t: 'Marketing',
          d: 'Mensual · always-on'
        }, {
          n: '05',
          t: '360°',
          d: 'Todo en uno'
        }].map((t, i) => <div key={i} className="bg-white rounded-sm p-3 border border-[var(--divider)]" data-id="element-1852">
              <div className="font-mono text-[8px] text-[var(--accent-blue)] tracking-widest mb-1 font-bold" data-id="element-1853">
                {t.n}
              </div>
              <div className="font-space font-bold text-sm leading-tight mb-0.5" data-id="element-1854">
                {t.t}
              </div>
              <div className="text-[9px] text-[var(--text-muted)]" data-id="element-1855">{t.d}</div>
            </div>)}
        </div>
      </div>

      {/* ============================================================ */}
      {/*  01 — WEB                                                    */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-1856">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1857">
          <div data-id="element-1858">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1859">
              01 · WEB
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1860">
              Sitios web.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1861">
              De la web Micro de €390 al Premium con reservas y blog. Cuatro
              tiers, una sola filosofía: que funcione.
            </div>
          </div>
          <div className="text-right" data-id="element-1862">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-1863">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-1864">
              €390
            </div>
          </div>
        </div>

        {/* Web Micro featured */}
        <div className="mb-4" data-id="element-1865">
          <PackCard tier="TIER 0" tierLabel="MICRO · LA PUERTA DE ENTRADA" name="Web Micro" tagline="Existo online. Pack intencionalmente limitado para negocios ultra-pequeños sin presupuesto. La puerta de entrada — no el destino." price="€390" timeline="48–72 horas" ideal="Frutería, peluquería de barrio, taller familiar, autónomos" warnings={['Página única (one-page), scroll vertical, sin navegación', 'Basado en plantilla — elegimos de 3 templates pre-hechos', 'Sin formulario — solo botones de Llamar y WhatsApp', 'Sin Google Maps integrado, sin email profesional', 'Sin hosting gestionado (cliente lo contrata aparte)', '0 revisiones, sin soporte post-entrega']} features={['Secciones básicas: nombre, horario, teléfono, dirección, descripción', 'Responsive (móvil + desktop)', 'Botón WhatsApp directo', 'Hasta 5 imágenes (propias o generadas con IA)', 'SEO básico (título + descripción)']} cta="Comprar Web Micro" accent="ink" data-id="element-1866" />
        </div>

        {/* Web Essentials / Pro / Premium row */}
        <div className="grid grid-cols-3 gap-4" data-id="element-1867">
          <PackCard tier="TIER 1" tierLabel="ESSENTIALS" name="Web Essentials" tagline="Landing funcional con formulario, mapa y SEO básico. La presencia digital mínima viable." price="€990" timeline="5–7 días" ideal="Peluquerías, talleres, fontaneros, electricistas" features={['1–2 secciones (Home + Contacto)', 'Responsive (móvil + desktop)', 'Formulario de contacto funcional', 'Integración con Google Maps', 'SEO básico (meta tags + sitemap)', 'Hosting gratuito 1er año (€60/año después)', 'SSL incluido', '1 ronda de revisión']} cta="Comprar Essentials" accent="blue" data-id="element-1868" />
          <PackCard tier="TIER 2" tierLabel="PROFESSIONAL" name="Web Professional" tagline="Sitio completo de 3–4 páginas con galería, optimización y soporte post-entrega." price="€2.400" timeline="10–14 días" ideal="Restaurantes, bares, tiendas, clínicas, estética" features={['Todo lo de Essentials, ampliado', '3–4 páginas (Home, Servicios, Galería, Contacto)', 'Hasta 20 imágenes optimizadas con IA', 'Google Business Profile optimizado', 'Menú digital / catálogo básico', 'Velocidad de carga optimizada (<2s)', '2 rondas de revisión', '1 mes de soporte post-entrega']} cta="Comprar Professional" accent="ink" data-id="element-1869" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Web Premium" tagline="5–6 páginas custom, reservas online, blog SEO, animaciones y 3 meses de soporte." price="€4.800" timeline="18–25 días" ideal="E-commerce, servicios premium, negocios que escalan" features={['Todo lo de Professional, ampliado', '5–6 páginas completamente personalizadas', 'Sistema de reservas online', 'Blog funcional con 3 artículos SEO iniciales', 'Integración WhatsApp Business', 'Animaciones y micro-interacciones', '3 rondas de revisión', '3 meses de soporte post-entrega', 'Capacitación 1 hora completa']} cta="Comprar Premium" accent="ink" data-id="element-1870" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  02 — BRAND                                                  */}
      {/* ============================================================ */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1871">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1872">
          <div data-id="element-1873">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1874">
              02 · BRAND
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1875">
              Identidad de marca.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1876">
              Logo, paleta, tipografía y sistema. Independiente del sitio web —
              tu marca viaja con todo lo demás.
            </div>
          </div>
          <div className="text-right" data-id="element-1877">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-1878">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-1879">
              €1.200
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-1880">
          <PackCard tier="TIER 1" tierLabel="BASICS" name="Brand Basics" tagline="Identidad básica rápida. Logo, paleta, tipografía y aplicaciones — listo para arrancar." price="€1.200" timeline="7–10 días" ideal="Startups, nuevos negocios, identidad básica rápido" features={['Logo principal (2 conceptos, 1 ronda)', 'Paleta de colores (3–4 colores)', 'Tipografía seleccionada (1–2 fuentes)', '10 aplicaciones mockup (tarjetas, social, etc.)', 'Brand guidelines PDF básico (5 páginas)', 'Archivos finales (PNG, SVG, PDF)']} cta="Comprar Brand Basics" accent="blue" data-id="element-1881" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Brand Premium" tagline="Identidad completa con estrategia, positioning, sub-marcas y 30+ aplicaciones." price="€5.500" timeline="20–28 días" ideal="B2B, startups tech, marcas que necesitan coherencia total" features={['Todo lo de Brand Basics, ampliado', '3 conceptos de logo (vs. 2)', 'Sub-marca / iconos adicionales', 'Brand strategy workshop (2 horas)', 'Positioning statement + messaging', 'Brand guidelines completo (15–20 páginas)', 'Templates de presentación', 'Email signature design', 'Social media templates', '30+ aplicaciones mockup']} cta="Comprar Brand Premium" accent="ink" data-id="element-1882" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  03 — CONTENT                                                */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-1883">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1884">
          <div data-id="element-1885">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1886">
              03 · CONTENT
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1887">
              Vídeo, foto y copy.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1888">
              Producción in-house para alimentar tu web, social y campañas.
              Rodamos en Barcelona y editamos en el mismo estudio.
            </div>
          </div>
          <div className="text-right" data-id="element-1889">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-1890">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-1891">
              €800
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-1892">
          <PackCard tier="CONTENT" tierLabel="STARTER" name="Content Starter" tagline="Lo mínimo para arrancar con contenido propio: 1 vídeo + 20 imágenes IA + copywriting para landing." price="€1.400" timeline="7–10 días" ideal="Negocios que estrenan web o lanzan producto" features={['1 vídeo corto (30–60s, edición simple)', '20 imágenes optimizadas con IA', 'Copywriting para 1 página (hasta 600 palabras)', '1 ronda de revisión por entregable', 'Archivos en formatos web + social']} cta="Comprar Starter" accent="blue" data-id="element-1893" />
          <PackCard tier="CONTENT" tierLabel="VIDEO" name="Video Production" tagline="Día de rodaje en Barcelona + edición. Dos vídeos terminados, listos para social y web." price="€1.800" timeline="10–14 días" ideal="Restaurantes, marcas con producto físico, eventos" features={['1 jornada de rodaje (8h) en Barcelona', 'Cámara, sonido e iluminación incluidos', '2 vídeos finales (versión corta + larga)', 'Edición, color y subtítulos', '2 rondas de revisión', 'Versiones para Instagram, TikTok, YouTube']} cta="Comprar Video" accent="ink" data-id="element-1894" />
          <PackCard tier="CONTENT" tierLabel="PHOTO" name="Photo Session" tagline="Media jornada de fotografía local. Foto de producto, equipo o espacio — lista para usar." price="€800" timeline="3–5 días" ideal="Tiendas, restaurantes, equipos pequeños" features={['Media jornada (4h) en Barcelona', 'Hasta 30 fotos editadas y entregadas', 'Retoque básico (luz, color, encuadre)', 'Versiones web y social', '1 ronda de selección']} cta="Comprar Photo" accent="blue" data-id="element-1895" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  04 — MARKETING                                              */}
      {/* ============================================================ */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1896">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1897">
          <div data-id="element-1898">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1899">
              04 · MARKETING
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1900">
              Always-on.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1901">
              Marketing mensual con foco en resultado, no en dashboard. Sin
              permanencia: pausas o cancelas con 30 días de aviso.
            </div>
          </div>
          <div className="text-right" data-id="element-1902">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-1903">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-1904">
              €350<span className="text-base" data-id="element-1905">/mes</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-1906">
          <PackCard tier="MONTHLY" tierLabel="SOCIAL" name="Social Management" tagline="Estrategia, calendario y publicación. Posts mensuales pensados para tu marca, no genéricos." price="€450/mes" timeline="Setup 7 días · Ongoing" ideal="Marcas locales, restaurantes, tiendas, servicios" features={['Audit inicial + estrategia trimestral', 'Calendario editorial mensual', '12 posts/mes (mix imagen + carrusel + reel)', 'Gestión de comentarios y DMs', 'Reporte mensual con learnings', 'Sin permanencia · 30 días aviso']} cta="Empezar Social" accent="blue" data-id="element-1907" />
          <PackCard tier="MONTHLY" tierLabel="SEO" name="SEO Monthly" tagline="Optimización técnica + contenido SEO. Sales de la página 2 de Google sin volver a verla." price="€350/mes" timeline="Audit 7 días · Ongoing" ideal="Negocios que quieren clientes vía búsqueda orgánica" features={['Audit técnico inicial completo', 'Investigación de keywords (mensual)', 'Optimización on-page continua', '1 artículo SEO/mes (800–1.200 palabras)', 'Link building básico', 'Reporte mensual con ranking + tráfico', 'Sin permanencia · 30 días aviso']} cta="Empezar SEO" accent="ink" data-id="element-1908" />
          <PackCard tier="MONTHLY" tierLabel="PAID" name="Paid Media" tagline="Meta + Google Ads gestionado. Setup limpio, optimización continua, reporte sin maquillaje." price="€600/mes" timeline="Setup 5–7 días · Ongoing" ideal="Negocios con presupuesto publicitario activo" features={['Setup inicial (Meta Ads + Google Ads)', 'Configuración de tracking y pixel', 'Hasta 3 campañas activas', 'Optimización semanal', 'Reporte mensual con CPL/CPA reales', 'Coste de medios aparte del fee', 'Sin permanencia · 30 días aviso']} cta="Empezar Paid" accent="ink" data-id="element-1909" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  05 — 360° PREMIUM COMBOS                                    */}
      {/* ============================================================ */}
      <div className="px-6 py-10" style={{
      background: '#1A2BFF'
    }} data-id="element-1910">
        <div className="mb-5 text-white flex items-baseline justify-between" data-id="element-1911">
          <div data-id="element-1912">
            <div className="font-mono text-[9px] opacity-75 tracking-widest mb-1" data-id="element-1913">
              05 · 360° · SISTEMA COMPLETO
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1914">
              Todo, en una sola línea.
            </div>
            <div className="text-[11px] opacity-85 mt-1 max-w-md" data-id="element-1915">
              Cuando necesitas brand, web y contenido a la vez. Mismo equipo,
              mismo plazo, ahorro real frente a contratar suelto.
            </div>
          </div>
          <div className="text-right text-white" data-id="element-1916">
            <div className="font-mono text-[9px] opacity-75 tracking-widest" data-id="element-1917">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl leading-none mt-1" data-id="element-1918">
              €3.500
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-1919">
          <PackCard tier="360°" tierLabel="BRAND + WEB" name="Brand + Web Combo" tagline="Identidad nueva y sitio que la usa desde el día 1. La opción cuando empiezas todo desde cero." price="€3.500" timeline="14–18 días" ideal="Nuevos negocios que necesitan todo desde cero" savings="Ahorro de €100 vs. comprar separado" combo features={['Brand Basics completo', 'Web Professional completo', 'Web integrada con la identidad desde día 1', 'Coherencia visual y verbal garantizada', 'Un solo equipo, un solo plazo']} cta="Comprar Combo" accent="ink" data-id="element-1920" />
          <PackCard tier="360°" tierLabel="FULL SYSTEM" name="Full System" tagline="Brand + Web + Content. Todo el sistema en un solo proyecto, con ahorro real." price="€8.900" timeline="25–30 días" ideal="Lanzamientos nuevos, rebrands completos" savings="Ahorro de €1.400 vs. comprar separado" combo features={['Brand Premium completo', 'Web Premium completo', 'Content Starter Pack:', '· 3 vídeos cortos (30–60s) para social/web', '· 40 imágenes profesionales con IA', '· Copywriting para todo el sitio', '· 5 posts de social media listos']} cta="Comprar Full System" accent="ink" data-id="element-1921" />
        </div>
      </div>

      {/* Add-ons */}
      <div className="px-6 py-10 bg-white" data-id="element-1922">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1923">
          <div data-id="element-1924">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1925">
              EXTRA · SERVICIOS ADICIONALES
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1926">
              Add-ons.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1927">
              Para clientes actuales o como upsell sobre un pack — añádelos a tu
              pedido o contrátalos por separado.
            </div>
          </div>
        </div>

        <div className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-1928">
          <div className="grid grid-cols-12 bg-[var(--text-ink)] text-white text-[9px] font-bold tracking-widest px-4 py-2.5" data-id="element-1929">
            <div className="col-span-6" data-id="element-1930">SERVICIO</div>
            <div className="col-span-2 text-right" data-id="element-1931">PRECIO</div>
            <div className="col-span-2 text-right" data-id="element-1932">PLAZO</div>
            <div className="col-span-2 text-right" data-id="element-1933">ACCIÓN</div>
          </div>
          {[['Pack imágenes IA (30 imágenes)', '€150', '2–3 días', false], ['Video corto (30–60s, edición simple)', '€350', '5–7 días', false], ['Copywriting (por página)', '€180', '2–3 días', false], ['Email marketing setup (Mailchimp/Klaviyo)', '€250', '3–4 días', false], ['Foto/video shoot local Barcelona', '€800', '1 día', false], ['Gestión redes sociales', '€450/mes', 'Ongoing', true], ['SEO mensual (básico)', '€350/mes', 'Ongoing', true], ['Mantenimiento web mensual', '€90/mes', 'Ongoing', true]].map(([name, price, time, recurring], i) => <div key={i} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--divider)] last:border-b-0 text-[10px] items-center" data-id="element-1934">
              <div className="col-span-6 flex items-center gap-2" data-id="element-1935">
                <span className="font-medium" data-id="element-1936">{name as string}</span>
                {recurring && <span className="text-[8px] font-mono text-[var(--accent-blue)] bg-[var(--bg-canvas)] px-1.5 py-0.5 rounded-full tracking-wider" data-id="element-1937">
                    RECURRENTE
                  </span>}
              </div>
              <div className="col-span-2 text-right font-space font-bold" data-id="element-1938">
                {price as string}
              </div>
              <div className="col-span-2 text-right text-[var(--text-muted)]" data-id="element-1939">
                {time as string}
              </div>
              <div className="col-span-2 text-right" data-id="element-1940">
                <button className="text-[9px] font-medium text-[var(--accent-blue)] underline" data-id="element-1941">
                  Añadir →
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {/* FAQ + Final CTA */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1942">
        <div className="grid grid-cols-12 gap-6" data-id="element-1943">
          <div className="col-span-7" data-id="element-1944">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1945">
              PREGUNTAS FRECUENTES
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-1946">
              Antes de comprar.
            </div>
            <div className="space-y-2" data-id="element-1947">
              {[['¿Los precios incluyen IVA?', 'Sí. Todos los packs muestran el precio final con IVA incluido. Lo que ves es lo que pagas.'], ['¿Cómo se paga?', '50% al inicio del proyecto y 50% antes de la entrega. Transferencia o tarjeta. Stripe.'], ['¿Y si necesito cambios después?', 'Cada pack incluye las revisiones especificadas. Revisiones extra se cotizan como add-on.'], ['¿Hay garantía?', '14 días de garantía post-entrega para corregir errores del trabajo realizado. Política completa en Términos y Condiciones.'], ['¿Puedo upgradear de un pack a otro?', 'Sí. El importe pagado del pack actual se descuenta del precio del pack superior si lo upgradeas en los primeros 60 días.']].map(([q, a], i) => <div key={i} className="bg-white rounded-sm border border-[var(--divider)] p-4" data-id="element-1948">
                  <div className="flex items-start justify-between gap-3" data-id="element-1949">
                    <div className="font-space font-bold text-sm" data-id="element-1950">{q}</div>
                    <MessageSquareIcon className="w-3.5 h-3.5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-1951" />
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] leading-relaxed mt-2" data-id="element-1952">
                    {a}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="col-span-5" data-id="element-1953">
            <div className="bg-[var(--text-ink)] text-white rounded-sm p-6 sticky top-4" data-id="element-1954">
              <div className="font-mono text-[9px] tracking-widest opacity-70 mb-2" data-id="element-1955">
                ¿NO LO TIENES CLARO?
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-3" data-id="element-1956">
                Empieza por una conversación.
              </div>
              <div className="text-[11px] opacity-80 leading-relaxed mb-4" data-id="element-1957">
                Cuéntanos qué necesitas, en 15 minutos te decimos qué pack
                encaja — o si necesitas uno custom.
              </div>
              <button className="w-full px-4 py-2.5 bg-white text-[var(--text-ink)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2 mb-2" data-id="element-1958">
                Agendar llamada · 15 min
                <ArrowRightIcon className="w-3 h-3" data-id="element-1959" />
              </button>
              <button className="w-full px-4 py-2.5 border border-white/30 rounded-full text-xs font-medium" data-id="element-1960">
                Enviar mensaje
              </button>
              <div className="border-t border-white/20 mt-5 pt-4 text-[9px] opacity-60 font-mono tracking-widest" data-id="element-1961">
                PAGO SEGURO · STRIPE · 50/50
              </div>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter data-id="element-1962" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  AI PACK CARD — setup + monthly dual pricing                        */
/* ================================================================== */
function AIPackCard({
  tier,
  tierLabel,
  name,
  tagline,
  setup,
  monthly,
  range,
  timeline,
  ideal,
  features,
  cta,
  accent,
  icon: Icon,
  flagship
}: {
  tier: string;
  tierLabel: string;
  name: string;
  tagline: string;
  setup?: string;
  monthly?: string;
  range?: string;
  timeline: string;
  ideal: string;
  features: string[];
  cta: string;
  accent: 'blue' | 'ink' | 'grey';
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  flagship?: boolean;
}) {
  const bg = accent === 'blue' ? '#1A2BFF' : accent === 'ink' ? '#0E0E12' : '#EDEEF1';
  const fg = accent === 'grey' ? '#0E0E12' : '#FFFFFF';
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-1963">
      {/* Header */}
      <div className="p-5" style={{
      background: bg,
      color: fg
    }} data-id="element-1964">
        <div className="flex items-center justify-between mb-3" data-id="element-1965">
          <div className="flex items-center gap-2" data-id="element-1966">
            <Icon className="w-3.5 h-3.5 opacity-90" strokeWidth={1.75} data-id="element-1967" />
            <div className="font-mono text-[9px] tracking-widest opacity-75" data-id="element-1968">
              {tier} · {tierLabel}
            </div>
          </div>
          {flagship && <div className="text-[8px] font-bold tracking-widest bg-white/15 px-2 py-0.5 rounded-full" data-id="element-1969">
              FLAGSHIP
            </div>}
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-1" data-id="element-1970">
          {name}
        </div>
        <div className="text-[11px] opacity-85 leading-relaxed mb-4" data-id="element-1971">
          {tagline}
        </div>

        {/* Dual pricing */}
        {range ? <div data-id="element-1972">
            <div className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-1973">
              {range}
            </div>
            <div className="text-[9px] font-mono tracking-wider opacity-75 mt-1" data-id="element-1974">
              PROYECTO + MENSUAL · IVA INCL.
            </div>
          </div> : <div className="grid grid-cols-2 gap-3" data-id="element-1975">
            <div className="border-r border-white/20 pr-3" data-id="element-1976">
              <div className="font-mono text-[8px] tracking-widest opacity-70 mb-1" data-id="element-1977">
                SETUP
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-none" data-id="element-1978">
                {setup}
              </div>
            </div>
            <div data-id="element-1979">
              <div className="font-mono text-[8px] tracking-widest opacity-70 mb-1" data-id="element-1980">
                MENSUAL
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-none" data-id="element-1981">
                {monthly || '—'}
              </div>
            </div>
          </div>}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col" data-id="element-1982">
        <div className="grid grid-cols-2 gap-3 pb-3 border-b border-[var(--divider)] mb-3 text-[10px]" data-id="element-1983">
          <div data-id="element-1984">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1985">
              PLAZO
            </div>
            <div className="font-medium" data-id="element-1986">{timeline}</div>
          </div>
          <div data-id="element-1987">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1988">
              IDEAL PARA
            </div>
            <div className="font-medium leading-snug" data-id="element-1989">{ideal}</div>
          </div>
        </div>

        <div data-id="element-1990">
          <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1991">
            INCLUYE
          </div>
          <ul className="space-y-1.5" data-id="element-1992">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug" data-id="element-1993">
                <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-1994" />
                <span data-id="element-1995">{f}</span>
              </li>)}
          </ul>
        </div>

        <div className="mt-5 pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-3" data-id="element-1996">
          <button className="flex-1 px-3 py-2 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-1997">
            {cta}
            <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-1998" />
          </button>
          <button className="px-3 py-2 border border-[var(--text-ink)] rounded-full text-[10px] font-medium" data-id="element-1999">
            Demo
          </button>
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  AI SOLUTIONS PAGE MOCKUP                                           */
/* ================================================================== */
function AIPageMockup() {
  return <BrowserFrame url="metroagency.co/ai" data-id="element-2000">
      <SiteNav active="ai" data-id="element-2001" />

      {/* Hero — dark with subtle network */}
      <div className="px-6 py-12 relative overflow-hidden" style={{
      background: '#0E0E12'
    }} data-id="element-2002">
        <div className="relative z-10" data-id="element-2003">
          <div className="flex items-center gap-2 mb-4" data-id="element-2004">
            <SparklesIcon className="w-3.5 h-3.5 text-[var(--accent-blue)]" strokeWidth={1.75} data-id="element-2005" />
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest" data-id="element-2006">
              NUEVA LÍNEA · AI SOLUTIONS
            </div>
          </div>
          <div className="font-space font-bold text-5xl leading-[0.95] tracking-tight text-white mb-4" data-id="element-2007">
            Tu negocio,
            <br data-id="element-2008" />
            <span className="text-[var(--accent-blue)]" data-id="element-2009">
              respondiendo a las 2am.
            </span>
          </div>
          <div className="font-helvetica text-sm text-white/70 leading-relaxed max-w-xl mb-6" data-id="element-2010">
            Chatbots que reservan, agentes que cualifican leads, flujos que
            ejecutan solos. Cinco servicios de IA con precio claro — setup más
            mensualidad, sin permanencia. Para negocios que quieren dejar de
            atender el WhatsApp manualmente.
          </div>
          <div className="flex items-center gap-3" data-id="element-2011">
            <button className="px-4 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2012">
              Hacer la auditoría · €490
              <ArrowRightIcon className="w-3 h-3" data-id="element-2013" />
            </button>
            <button className="px-4 py-2.5 border border-white/30 text-white rounded-full text-xs font-medium" data-id="element-2014">
              Ver todos los servicios
            </button>
          </div>

          {/* Mini stats */}
          <div className="grid grid-cols-4 gap-4 mt-10 pt-6 border-t border-white/15" data-id="element-2015">
            {[['5', 'Servicios de IA'], ['€490', 'Punto de entrada'], ['24/7', 'Sin horario'], ['0', 'Permanencia']].map(([n, l]) => <div key={l} data-id="element-2016">
                <div className="font-space font-bold text-3xl text-white tracking-tight leading-none" data-id="element-2017">
                  {n}
                </div>
                <div className="font-mono text-[9px] text-white/50 tracking-widest mt-1" data-id="element-2018">
                  {l}
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  01 — CHATBOT AI                                             */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-2019">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2020">
          <div data-id="element-2021">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2022">
              <MessageSquareIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2023" />
              01 · CHATBOT AI
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2024">
              Atención automática.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2025">
              Un bot que responde como tú lo harías — pero a cualquier hora.
              Conectado a tu web, WhatsApp o redes.
            </div>
          </div>
          <div className="text-right" data-id="element-2026">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2027">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2028">
              €490
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-2029">
          <AIPackCard tier="TIER 1" tierLabel="BASIC" name="Chatbot Basic" tagline="Un canal, FAQs y captación de leads. La primera capa de IA — la que paga sola." setup="€490" monthly="€49/mes" timeline="5–7 días" ideal="Pequeño negocio, autónomos, servicios locales" features={['1 canal (web o WhatsApp)', 'Hasta 500 conversaciones/mes', 'Entrenamiento con tu FAQ y servicios', 'Captación de email/teléfono del lead', 'Handoff a humano cuando bloquea', 'Reporte mensual de conversaciones']} cta="Comprar Basic" accent="ink" icon={MessageSquareIcon} data-id="element-2030" />
          <AIPackCard tier="TIER 2" tierLabel="PROFESSIONAL" name="Chatbot Professional" tagline="Dos canales, reservas integradas y memoria de cliente. Para negocios con flujo real." setup="€1.200" monthly="€129/mes" timeline="10–14 días" ideal="Clínicas, restaurantes, estética, servicios" features={['Todo lo de Basic, ampliado', '2 canales (web + WhatsApp o Instagram)', 'Reservas integradas con tu calendario', 'Memoria de cliente entre conversaciones', 'Mensajes proactivos (recordatorios, follow-ups)', 'Hasta 2.000 conversaciones/mes', 'Dashboard con métricas']} cta="Comprar Pro" accent="blue" icon={MessageSquareIcon} flagship data-id="element-2031" />
          <AIPackCard tier="TIER 3" tierLabel="ENTERPRISE" name="Chatbot Enterprise" tagline="Omnicanal con CRM, recomendaciones y voz de marca custom. Para e-commerce y operaciones serias." setup="€2.800" monthly="€290/mes" timeline="18–25 días" ideal="E-commerce, marcas con múltiples canales" features={['Todo lo de Professional, ampliado', 'Omnicanal (web + WhatsApp + IG + email)', 'Integración CRM (HubSpot, Pipedrive, Salesforce)', 'Recomendaciones de producto con IA', 'Voz de marca custom · GPT entrenado', 'Conversaciones ilimitadas', 'A/B testing de respuestas', 'SLA + soporte prioritario']} cta="Comprar Enterprise" accent="ink" icon={MessageSquareIcon} data-id="element-2032" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  02 — WORKFLOW AUTOMATION                                    */}
      {/* ============================================================ */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2033">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2034">
          <div data-id="element-2035">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2036">
              <GitBranchIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2037" />
              02 · WORKFLOW AUTOMATION
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2038">
              Las tareas, en piloto automático.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2039">
              Lo que haces a mano todos los días — facturación, follow-up,
              reporting — corriendo solo en background.
            </div>
          </div>
          <div className="text-right" data-id="element-2040">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2041">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2042">
              €350
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-2043">
          <AIPackCard tier="TIER 1" tierLabel="STARTER" name="Automation Starter" tagline="Un flujo, una integración. La primera tarea repetitiva que sale de tu agenda." setup="€350" timeline="3–5 días" ideal="Autónomos que quieren probar automatización" features={['1 flujo de trabajo automatizado', 'Integración entre 2 herramientas (ej. form → email)', 'Notificaciones automáticas', 'Documentación del flujo', 'Sin coste mensual · pago único', '14 días de soporte post-entrega']} cta="Comprar Starter" accent="ink" icon={GitBranchIcon} data-id="element-2044" />
          <AIPackCard tier="TIER 2" tierLabel="BUSINESS" name="Automation Business" tagline="Tres flujos conectados, dashboard de seguimiento y mantenimiento mensual." setup="€900" monthly="€150/mes" timeline="7–10 días" ideal="PYMEs con procesos repetitivos claros" features={['Hasta 3 flujos de trabajo conectados', 'Integración con 4–5 herramientas', 'Lead routing y scoring básico', 'Reporting automatizado (semanal/mensual)', 'Dashboard con estado de los flujos', 'Mantenimiento + ajustes mensuales']} cta="Comprar Business" accent="blue" icon={GitBranchIcon} flagship data-id="element-2045" />
          <AIPackCard tier="TIER 3" tierLabel="FULL SYSTEM" name="Automation Full System" tagline="Toda la operación conectada. IA para decisiones, no solo triggers. El back-office sin manos." setup="€2.400" monthly="€350/mes" timeline="15–20 días" ideal="Empresas con operación compleja y escalable" features={['Flujos ilimitados, end-to-end', 'Integración con todo el stack (CRM, ERP, billing)', 'Decisiones con IA (priorización, ruteo, scoring)', 'Alertas inteligentes y escalado', 'Auditoría trimestral y optimización', 'Soporte prioritario + SLA']} cta="Comprar Full System" accent="ink" icon={GitBranchIcon} data-id="element-2046" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  03 — AI AGENTS                                              */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-2047">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2048">
          <div data-id="element-2049">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2050">
              <UsersIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2051" />
              03 · AI AGENTS
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2052">
              Empleados que no duermen.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2053">
              Agentes autónomos para tareas concretas — cualificar leads,
              atender post-venta, generar reportes. Construidos a medida.
            </div>
          </div>
          <div className="text-right" data-id="element-2054">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2055">
              RANGO
            </div>
            <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2056">
              €3.500–€8.000
            </div>
            <div className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider mt-1" data-id="element-2057">
              + €200–500/mes
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4" data-id="element-2058">
          <div className="col-span-7" data-id="element-2059">
            <AIPackCard tier="CUSTOM" tierLabel="PROJECT" name="AI Agent Custom" tagline="Un agente entrenado para una tarea específica de tu negocio. Cualifica, atiende, reporta — solo." range="€3.500–€8.000" timeline="3–6 semanas" ideal="Equipos con tareas de alto volumen y baja variabilidad" features={['Discovery + definición del scope del agente', 'Entrenamiento con tus datos, procesos y voz de marca', 'Integración con tu CRM/herramientas existentes', 'Hand-off claro a humano cuando bloquea', 'Dashboard con métricas y conversaciones', 'Iteración continua (los primeros 3 meses)', 'Mensualidad de mantenimiento: €200–500']} cta="Pedir presupuesto" accent="blue" icon={UsersIcon} data-id="element-2060" />
          </div>
          <div className="col-span-5 space-y-3" data-id="element-2061">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2062">
              CASOS TÍPICOS
            </div>
            {[['Lead qualification', 'Califica leads entrantes, los puntúa y agenda la llamada solo si pasan el filtro.'], ['Atención post-venta', 'Responde devoluciones, tickets de estado y consultas frecuentes sin escalado.'], ['Reporting automático', 'Recoge datos de tu stack y genera el reporte semanal sin que nadie lo escriba.'], ['Outreach personalizado', 'Investiga el lead, redacta el primer email custom y agenda follow-up.']].map(([k, v]) => <div key={k} className="border border-[var(--divider)] rounded-sm p-3 bg-[var(--bg-canvas)]" data-id="element-2063">
                <div className="font-space font-bold text-sm leading-tight mb-1" data-id="element-2064">
                  {k}
                </div>
                <div className="text-[10px] text-[var(--text-muted)] leading-snug" data-id="element-2065">
                  {v}
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  04 — AI CONSULTING                                          */}
      {/* ============================================================ */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2066">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2067">
          <div data-id="element-2068">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2069">
              <LightbulbIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2070" />
              04 · AI CONSULTING
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2071">
              Antes de automatizar, entender.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2072">
              Para empresas que ven la ola de IA y no saben por dónde entrar.
              Auditoría puntual o retainer mensual.
            </div>
          </div>
          <div className="text-right" data-id="element-2073">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2074">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2075">
              €490
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-2076">
          <AIPackCard tier="ONE-OFF" tierLabel="AUDIT" name="AI Audit" tagline="Una sesión, un informe. Te decimos dónde la IA te ahorra dinero — y dónde no merece la pena." setup="€490" timeline="5–7 días" ideal="Empresas que quieren empezar con IA pero no saben por dónde" features={['Sesión de discovery (90 min)', 'Análisis de procesos actuales', 'Mapa de oportunidades de IA priorizado', 'Estimación de ahorro por automatización', 'Recomendación de stack tecnológico', 'Informe PDF + presentación (45 min)', 'Sin compromiso de continuidad']} cta="Comprar Audit" accent="ink" icon={LightbulbIcon} data-id="element-2077" />
          <AIPackCard tier="ONGOING" tierLabel="RETAINER" name="AI Strategy Retainer" tagline="Tu socio de IA durante 6+ meses. Roadmap, decisiones de stack y supervisión de implementación." monthly="€600/mes" timeline="Setup 7 días · Ongoing" ideal="Equipos en transformación digital con IA como pilar" features={['2h/semana de consulting (videollamada o async)', 'Roadmap de IA trimestral', 'Selección y validación de herramientas', 'Supervisión de implementaciones', 'Revisión de prompts y outputs de IA', 'Acceso prioritario al equipo Metro', 'Sin permanencia · 30 días aviso']} cta="Empezar Retainer" accent="blue" icon={LightbulbIcon} flagship data-id="element-2078" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  05 — AI TOOLS SETUP                                         */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-2079">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2080">
          <div data-id="element-2081">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2082">
              <WrenchIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2083" />
              05 · AI TOOLS SETUP
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2084">
              Tus herramientas, con voz de marca.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2085">
              GPTs custom entrenados con tu marca y setups de las herramientas
              de IA que ya usas — bien configuradas, no a medias.
            </div>
          </div>
          <div className="text-right" data-id="element-2086">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2087">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2088">
              €200
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-2089">
          <AIPackCard tier="GPT" tierLabel="CUSTOM" name="Custom GPTs" tagline="Un GPT entrenado con tu voz, tus FAQs y tus guidelines. Para tu equipo, no para el mundo." range="€250–€600" timeline="5–10 días" ideal="Equipos internos · Brand managers · Atención cliente" features={['Discovery del use-case (1 sesión)', 'Entrenamiento con docs, brand book y FAQ', 'Voz de marca aplicada a las respuestas', 'Hasta 3 rondas de ajuste', 'Documentación de uso para tu equipo', 'Funciona en ChatGPT Team/Enterprise']} cta="Pedir GPT custom" accent="ink" icon={SparklesIcon} data-id="element-2090" />
          <AIPackCard tier="SETUP" tierLabel="TOOLS" name="AI Tool Setup" tagline="Configuramos las herramientas que ya pagas (Notion AI, Make, Zapier, Claude, etc.) bien hechas." range="€200–€500" timeline="3–7 días" ideal="Empresas con stack de IA mal configurado" features={['Setup de la herramienta elegida', 'Conexiones e integraciones básicas', 'Plantillas y prompts iniciales', 'Sesión de formación con tu equipo (1h)', 'Documentación de uso', 'Soporte post-setup 14 días']} cta="Pedir setup" accent="ink" icon={WrenchIcon} data-id="element-2091" />
        </div>
      </div>

      {/* ============================================================ */}
      {/*  UPSELL PATHS — connection to existing services              */}
      {/* ============================================================ */}
      <div className="px-6 py-12" style={{
      background: '#0E0E12'
    }} data-id="element-2092">
        <div className="text-white mb-6" data-id="element-2093">
          <div className="flex items-center gap-2 mb-2" data-id="element-2094">
            <ZapIcon className="w-3.5 h-3.5 text-[var(--accent-blue)]" strokeWidth={1.75} data-id="element-2095" />
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest" data-id="element-2096">
              CONEXIONES NATURALES
            </div>
          </div>
          <div className="font-space font-bold text-3xl tracking-tight leading-tight mb-2" data-id="element-2097">
            Tu servicio actual + IA.
          </div>
          <div className="text-[11px] opacity-70 leading-relaxed max-w-lg" data-id="element-2098">
            Cada servicio Metro tiene un upgrade lógico con IA. No es venderte
            más — es resolver el problema que ya tienes con la herramienta
            adecuada.
          </div>
        </div>

        <div className="space-y-3" data-id="element-2099">
          {[{
          from: 'Web Professional',
          fromPrice: '€990',
          trigger: '¿Quieres que tu web atienda clientes a las 2am?',
          to: 'Chatbot Basic',
          toPrice: '€490'
        }, {
          from: 'Social Management',
          fromPrice: '€450/mes',
          trigger: 'Estás respondiendo los DMs manualmente.',
          to: 'Chatbot Basic · DMs',
          toPrice: '€490'
        }, {
          from: 'Brand Basics',
          fromPrice: '€1.200',
          trigger: 'Tu marca tiene voz. ¿Quieres un GPT que la conozca?',
          to: 'Custom GPT',
          toPrice: '€400'
        }, {
          from: 'Cualquier servicio',
          fromPrice: '—',
          trigger: '¿Quieres saber dónde la IA te ahorra dinero?',
          to: 'AI Audit',
          toPrice: '€490'
        }].map((p, i) => <div key={i} className="grid grid-cols-12 gap-3 items-center bg-white/5 border border-white/10 rounded-sm px-4 py-3" data-id="element-2100">
              {/* From */}
              <div className="col-span-3" data-id="element-2101">
                <div className="font-mono text-[8px] text-white/40 tracking-widest mb-1" data-id="element-2102">
                  TIENES
                </div>
                <div className="font-space font-bold text-sm text-white leading-tight" data-id="element-2103">
                  {p.from}
                </div>
                <div className="text-[10px] text-white/50 font-mono mt-0.5" data-id="element-2104">
                  {p.fromPrice}
                </div>
              </div>

              {/* Trigger */}
              <div className="col-span-6 text-center" data-id="element-2105">
                <div className="font-mono text-[8px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2106">
                  PREGUNTA
                </div>
                <div className="font-helvetica italic text-[12px] text-white leading-snug" data-id="element-2107">
                  "{p.trigger}"
                </div>
              </div>

              {/* Arrow + To */}
              <div className="col-span-3 flex items-center justify-end gap-2" data-id="element-2108">
                <ArrowRightIcon className="w-3.5 h-3.5 text-[var(--accent-blue)] flex-shrink-0" data-id="element-2109" />
                <div className="text-right" data-id="element-2110">
                  <div className="font-mono text-[8px] text-white/40 tracking-widest mb-1" data-id="element-2111">
                    AÑADES
                  </div>
                  <div className="font-space font-bold text-sm text-white leading-tight" data-id="element-2112">
                    {p.to}
                  </div>
                  <div className="text-[10px] text-[var(--accent-blue)] font-mono font-medium mt-0.5" data-id="element-2113">
                    {p.toPrice}
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        <div className="mt-6 text-[10px] text-white/50 leading-relaxed max-w-xl" data-id="element-2114">
          Si ya eres cliente Metro, puedes añadir cualquier servicio de IA como
          add-on a tu pack con descuento del 10% el primer trimestre.
        </div>
      </div>

      {/* FAQ + Final CTA */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-2115">
        <div className="grid grid-cols-12 gap-6" data-id="element-2116">
          <div className="col-span-7" data-id="element-2117">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2118">
              PREGUNTAS FRECUENTES · AI
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-2119">
              Antes de empezar.
            </div>
            <div className="space-y-2" data-id="element-2120">
              {[['¿Qué modelos usáis por debajo?', 'Depende del caso: GPT-4 / Claude para conversación, Gemini para multimedia, modelos open-source en local cuando hay datos sensibles. Te lo explicamos antes de firmar.'], ['¿Dónde se guardan los datos de mis clientes?', 'Servidores en UE conforme RGPD. Los datos no se usan para entrenar modelos públicos. Acuerdo de procesamiento de datos firmado.'], ['¿Y si el bot dice algo mal?', 'Configuramos límites estrictos y un fallback a humano cuando bloquea. Revisamos las conversaciones del primer mes contigo para ajustar tono y respuestas.'], ['¿Cómo se factura el mensual?', 'Cuota fija que cubre infraestructura, modelo y mantenimiento. Sin sorpresas. Si superas el volumen del plan, te avisamos antes — nunca facturamos overage sin permiso.'], ['¿Puedo cancelar el mensual cuando quiera?', 'Sí. Sin permanencia. 30 días de aviso. Te entregamos las configuraciones para que puedas seguir solo si lo decides.']].map(([q, a], i) => <div key={i} className="bg-white rounded-sm border border-[var(--divider)] p-4" data-id="element-2121">
                  <div className="flex items-start justify-between gap-3" data-id="element-2122">
                    <div className="font-space font-bold text-sm" data-id="element-2123">{q}</div>
                    <MessageSquareIcon className="w-3.5 h-3.5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-2124" />
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] leading-relaxed mt-2" data-id="element-2125">
                    {a}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="col-span-5" data-id="element-2126">
            <div className="bg-[var(--accent-blue)] text-white rounded-sm p-6 sticky top-4" data-id="element-2127">
              <div className="font-mono text-[9px] tracking-widest opacity-80 mb-2" data-id="element-2128">
                NO SABES POR DÓNDE EMPEZAR
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-3" data-id="element-2129">
                Empieza por el Audit.
              </div>
              <div className="text-[11px] opacity-90 leading-relaxed mb-4" data-id="element-2130">
                €490, una sesión, un informe priorizado. En 5–7 días sabes qué
                automatizar primero y qué dejar a humanos. Sin compromiso.
              </div>
              <button className="w-full px-4 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2 mb-2" data-id="element-2131">
                Comprar AI Audit · €490
                <ArrowRightIcon className="w-3 h-3" data-id="element-2132" />
              </button>
              <button className="w-full px-4 py-2.5 border border-white/40 rounded-full text-xs font-medium" data-id="element-2133">
                Agendar llamada · 15 min
              </button>
              <div className="border-t border-white/20 mt-5 pt-4 text-[9px] opacity-70 font-mono tracking-widest" data-id="element-2134">
                DATOS EN UE · RGPD · SIN PERMANENCIA
              </div>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter data-id="element-2135" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  TERMS & CONDITIONS PAGE MOCKUP                                     */
/* ================================================================== */
function TermsPageMockup() {
  return <BrowserFrame url="metroagency.co/terminos" data-id="element-2136">
      <SiteNav active="terms" data-id="element-2137" />

      {/* Hero */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2138">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2139">
          LEGAL · METRO AGENCY S.L.
        </div>
        <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-2140">
          Términos
          <br data-id="element-2141" />y condiciones.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2142">
          Las reglas del juego, en castellano y sin letra pequeña confusa. Si
          algo no está claro, pregúntanos antes de firmar.
        </div>
        <div className="mt-4 flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-2143">
          <span className="font-mono tracking-wider" data-id="element-2144">
            ÚLTIMA ACTUALIZACIÓN · [ FECHA ]
          </span>
          <span data-id="element-2145">·</span>
          <span className="font-mono tracking-widest" data-id="element-2146">v1.0</span>
        </div>
      </div>

      {/* Layout: TOC + content */}
      <div className="px-6 py-10 grid grid-cols-12 gap-6" data-id="element-2147">
        {/* TOC */}
        <div className="col-span-4" data-id="element-2148">
          <div className="sticky top-4" data-id="element-2149">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2150">
              ÍNDICE
            </div>
            <div className="space-y-1.5" data-id="element-2151">
              {['01 · Sobre Metro Agency', '02 · Servicios contratados', '03 · Condiciones de pago', '04 · Plazos y entregas', '05 · Revisiones y cambios', '06 · Política de devoluciones', '07 · Garantía post-entrega', '08 · Propiedad intelectual', '09 · Confidencialidad', '10 · Cancelaciones', '11 · Limitación de responsabilidad', '12 · Protección de datos · RGPD', '13 · Cookies', '14 · Modificaciones', '15 · Jurisdicción aplicable'].map((t, i) => <a key={i} className={'block text-[10px] py-1 ' + (i === 2 ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]')} data-id="element-2152">
                  {t}
                </a>)}
            </div>
            <div className="mt-5 pt-4 border-t border-[var(--divider)]" data-id="element-2153">
              <div className="text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-2154">
                ¿Dudas legales?
                <br data-id="element-2155" />
                <span className="text-[var(--accent-blue)] font-medium" data-id="element-2156">
                  legal@metroagency.co
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="col-span-8 space-y-6" data-id="element-2157">
          {[{
          n: '01',
          t: 'Sobre Metro Agency',
          p: 'Metro Agency S.L., con NIF B12345678 y domicilio social en Carrer de la Marina, 145, 08013 Barcelona, España, es la entidad responsable de los servicios contratados a través de este sitio. Inscrita en el Registro Mercantil de Barcelona. [ Texto legal pendiente de validación por asesoría. ]'
        }, {
          n: '02',
          t: 'Servicios contratados',
          p: 'Los packs descritos en metroagency.co/packs constituyen una oferta cerrada con alcance, plazo y precio especificados. La contratación implica la aceptación íntegra de los presentes términos. [ Detalle pendiente sobre tipos de servicio, perímetro de cada pack, exclusiones generales. ]'
        }, {
          n: '03',
          t: 'Condiciones de pago',
          p: 'Todos los precios mostrados incluyen IVA al tipo general del 21% conforme a la Ley 37/1992. El pago se estructura habitualmente en dos hitos — 50% al inicio del proyecto y 50% antes de la entrega final. Métodos aceptados: transferencia bancaria SEPA y tarjeta a través de Stripe. [ Detalle pendiente: plazos exactos, intereses de demora aplicables conforme a Ley 3/2004, recargo por impago. ]'
        }, {
          n: '04',
          t: 'Plazos y entregas',
          p: 'Los plazos indicados en cada pack son estimaciones en días laborables sujetas a la entrega puntual de contenidos, accesos y aprobaciones por parte del cliente. Cualquier retraso imputable al cliente prorrogará el calendario en igual medida. [ Detalle pendiente sobre causas de fuerza mayor. ]'
        }, {
          n: '05',
          t: 'Revisiones y cambios',
          p: 'Cada pack incluye un número específico de rondas de revisión. Las revisiones adicionales se facturan como add-on al precio vigente. Los cambios fuera del alcance original requieren una orden de cambio firmada. [ Detalle pendiente sobre qué constituye "fuera de alcance" vs. "ajuste razonable". ]'
        }, {
          n: '06',
          t: 'Política de devoluciones',
          p: 'Conforme al artículo 103.a del Real Decreto Legislativo 1/2007 (Ley General para la Defensa de los Consumidores), los servicios digitales personalizados quedan excluidos del derecho de desistimiento una vez iniciada la ejecución. Para servicios no iniciados se aplica el derecho de desistimiento de 14 días naturales. [ Detalle pendiente: condiciones específicas por pack, devoluciones parciales, depósito no reembolsable. ]'
        }, {
          n: '07',
          t: 'Garantía post-entrega',
          p: 'Se aplica una garantía de 14 días naturales desde la fecha de entrega para corregir defectos imputables al trabajo realizado por Metro Agency S.L. La garantía no cubre errores derivados de modificaciones posteriores realizadas por el cliente o terceros. [ Texto legal pendiente. ]'
        }, {
          n: '08',
          t: 'Propiedad intelectual',
          p: 'Los derechos de propiedad intelectual sobre los entregables finales se transfieren al cliente en el momento del pago íntegro del proyecto. Metro Agency S.L. conserva el derecho a utilizar el trabajo realizado en su portfolio y materiales de comunicación, salvo pacto contrario por escrito. [ Detalle pendiente sobre licencias de software, fuentes, imágenes con licencia. ]'
        }, {
          n: '09',
          t: 'Confidencialidad',
          p: '[ Texto pendiente: tratamiento de información sensible compartida durante el proyecto. NDA implícito por defecto. Duración del compromiso post-proyecto. ]'
        }, {
          n: '10',
          t: 'Cancelaciones',
          p: '[ Texto pendiente: condiciones de cancelación por parte del cliente, importes no reembolsables, facturación del trabajo realizado hasta la fecha, indemnización por resolución unilateral. ]'
        }, {
          n: '11',
          t: 'Limitación de responsabilidad',
          p: '[ Texto pendiente: límite de responsabilidad al importe efectivamente facturado, exclusión de daños indirectos, lucro cesante y pérdida de oportunidad. ]'
        }, {
          n: '12',
          t: 'Protección de datos · RGPD',
          p: 'El tratamiento de datos personales se realiza conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018 (LOPDGDD). Los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad pueden ejercerse escribiendo a privacy@metroagency.co. [ Texto completo pendiente. ]'
        }, {
          n: '13',
          t: 'Cookies',
          p: '[ Política de cookies pendiente — cookies técnicas, de analítica, de marketing. Configuración disponible en el banner del sitio. ]'
        }, {
          n: '14',
          t: 'Modificaciones',
          p: '[ Metro Agency se reserva el derecho a modificar los presentes términos. Las modificaciones se notifican con antelación a los clientes con proyectos en curso. ]'
        }, {
          n: '15',
          t: 'Jurisdicción aplicable',
          p: 'Las partes, con renuncia expresa a cualquier otro fuero, se someten a los Juzgados y Tribunales de Barcelona para la resolución de cualquier controversia derivada de la relación contractual.'
        }].map(s => <div key={s.n} className="border-t border-[var(--divider)] pt-4" data-id="element-2158">
              <div className="flex items-baseline gap-3 mb-2" data-id="element-2159">
                <span className="font-mono text-[10px] font-bold text-[var(--accent-blue)] tracking-widest" data-id="element-2160">
                  {s.n}
                </span>
                <div className="font-space font-bold text-lg leading-tight tracking-tight" data-id="element-2161">
                  {s.t}
                </div>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-2162">
                {s.p}
              </div>
            </div>)}

          {/* Acceptance */}
          <div className="text-white p-5 mt-4" style={{
          background: '#1A2BFF'
        }} data-id="element-2163">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-2164">
              ACEPTACIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-2165">
              La contratación de cualquier pack implica la aceptación de estos
              términos.
            </div>
            <div className="text-[11px] opacity-90 leading-relaxed" data-id="element-2166">
              Si algo no te encaja antes de firmar, escríbenos a
              legal@metroagency.co y lo hablamos. Preferimos resolver dudas en
              la conversación que en una factura.
            </div>
          </div>
        </div>
      </div>

      <SiteFooter data-id="element-2167" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  Page section wrapper with notes                                    */
/* ================================================================== */
function PageRedesignBlock({
  number,
  title,
  url,
  intro,
  notes,
  mockup
}: {
  number: string;
  title: string;
  url: string;
  intro: string;
  notes: {
    tag: string;
    title: string;
    body: string;
  }[];
  mockup: React.ReactNode;
}) {
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-id="element-2168">
      <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6" data-id="element-2169">
        <div data-id="element-2170">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-3" data-id="element-2171">
            {number}
          </div>
          <h3 className="font-space text-4xl font-bold tracking-tight leading-[0.95] mb-2" data-id="element-2172">
            {title}
          </h3>
          <div className="font-mono text-xs text-[var(--accent-blue)] tracking-wider" data-id="element-2173">
            {url}
          </div>
          <p className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed mt-4" data-id="element-2174">
            {intro}
          </p>
        </div>

        <div className="space-y-3" data-id="element-2175">
          <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest" data-id="element-2176">
            CAMBIOS CLAVE
          </div>
          {notes.map((n, i) => <div key={i} className="border-l-2 border-[var(--accent-blue)] pl-4 py-1" data-id="element-2177">
              <div className="flex items-center gap-2 mb-1" data-id="element-2178">
                <span className="font-mono text-[9px] text-[var(--accent-blue)] font-bold tracking-widest" data-id="element-2179">
                  {n.tag}
                </span>
              </div>
              <div className="font-space font-medium text-sm leading-tight mb-1" data-id="element-2180">
                {n.title}
              </div>
              <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed" data-id="element-2181">
                {n.body}
              </div>
            </div>)}
        </div>
      </div>

      <div className="lg:col-span-7" data-id="element-2182">{mockup}</div>
    </div>;
}
/* ================================================================== */
/*  Section                                                            */
/* ================================================================== */
export function WebsiteRedesignSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="website" ref={ref} className="min-h-screen px-8 py-24" data-id="element-2183">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-2184">
        <div className="mb-16 max-w-3xl" data-id="element-2185">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-2186">
            10 / WEBSITE REDESIGN
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-2187">
            The brand, online.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-2188">
            Las tres páginas principales del sitio reconstruidas en el nuevo
            sistema. Voz 2026 en castellano, paleta y tipografía actualizadas,
            CTAs nuevas, y la metáfora del metro deepened — sin "magia", sin
            "wow", sin exclamaciones forzadas. Pensado para Squarespace: solo
            bloques estándar (hero, columnas, accordion, formulario, footer)
            para que sea editable sin código.
          </p>
        </div>

        {/* Constraints strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-2189">
          {[{
          t: 'Idioma principal',
          v: 'Castellano',
          d: 'Inglés como secundario · CTAs en EN cuando funcionan mejor'
        }, {
          t: 'Sistema',
          v: 'Squarespace',
          d: 'Bloques estándar, sin custom code'
        }, {
          t: 'Paleta',
          v: 'Blue · Ink · Cool grey',
          d: 'Secundarios solo en gráficos de servicios'
        }, {
          t: 'Voz',
          v: '2026',
          d: 'Sin "magic" · sin "wow" · puntos en lugar de exclamaciones'
        }].map(c => <div key={c.t} className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)]" data-id="element-2190">
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2191">
                {c.t.toUpperCase()}
              </div>
              <div className="font-space font-bold text-base leading-tight mb-1" data-id="element-2192">
                {c.v}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-2193">
                {c.d}
              </div>
            </div>)}
        </div>

        <div className="space-y-24" data-id="element-2194">
          {/* 10.1 — Home */}
          <PageRedesignBlock number="10.1" title="Inicio" url="metroagency.co" intro="La home pasa del crema con mapa de metro a un sistema más sobrio: gris frío como fondo, mapa traducido a red de servicios (estrategia / creatividad / producción / performance), y la nueva tagline en castellano como pieza central." notes={[{
          tag: 'HERO',
          title: '"Donde las ideas conectan."',
          body: 'La tagline 2026 sustituye al texto largo anterior. Eyebrow corto en azul, párrafo de apoyo en gris para que el titular respire.'
        }, {
          tag: 'CTA',
          title: '"Build something that moves people"',
          body: 'CTA hero — la única que mantiene inglés porque tiene doble lectura (mover personas + tránsito). El secundario en castellano: "Ver trabajos".'
        }, {
          tag: 'VISUAL',
          title: 'Red de servicios, no mapa decorativo',
          body: 'Las cuatro líneas representan estrategia, creatividad, producción y performance. Las estaciones son donde se cruzan.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque azul a sangre',
          body: 'Las 80 palabras del manifiesto se introducen aquí en versión corta. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'CIERRE',
          title: '"Start the conversation"',
          body: 'CTA final más blanda que la del hero. Sin formularios largos en home — solo invitación.'
        }]} mockup={<HomePageMockup data-id="element-2196" />} data-id="element-2195" />

          {/* 10.2 — Services */}
          <PageRedesignBlock number="10.2" title="Servicios" url="metroagency.co/servicios" intro="El hero azul se mantiene como en la versión actual (era lo más fuerte del sitio), pero la copy cambia a la metáfora de línea/estaciones. Los tres bloques actuales se reordenan en cuatro estaciones de servicio y los accordion se convierten en cards expandibles con checklist." notes={[{
          tag: 'HERO',
          title: '"Una línea. Cuatro estaciones."',
          body: 'Mantiene el azul a sangre del actual pero la copy se ajusta al sistema verbal nuevo. Eyebrow + headline + subhead corto.'
        }, {
          tag: 'ESTRUCTURA',
          title: 'De 3 accordion a 3 cards con checklist',
          body: 'Squarespace no permite UX complejo en accordion, así que cada categoría es una card con número grande + título + sublistado siempre visible.'
        }, {
          tag: 'CATEGORÍAS',
          title: 'Creatividad · Marketing & Performance · Producción',
          body: 'Mismas tres familias que el sitio actual, pero renombradas para alinear con el manifesto. Cada una con 4 bullets concretos.'
        }, {
          tag: 'APPROACH',
          title: 'Bloque "Cómo trabajamos"',
          body: 'Tres cards horizontales con los diferenciadores: un equipo, un contacto, un lenguaje. Refuerza la propuesta end-to-end sin usar la palabra "integral".'
        }, {
          tag: 'CTA',
          title: '"Map your next campaign"',
          body: 'El CTA del set 2026 más relevante para esta página — sugiere planificación, no venta. Termina la página sin presión.'
        }]} mockup={<ServicesPageMockup data-id="element-2198" />} data-id="element-2197" />

          {/* 10.3 — About */}
          <PageRedesignBlock number="10.3" title="Nosotros" url="metroagency.co/nosotros" intro="La página About reemplaza el círculo decorativo del logo con un manifiesto a sangre en negro. Recupera la historia de fundación (Barcelona, dificultad para encontrar una agencia end-to-end) y la combina con el sistema verbal nuevo, la matriz de equipo y los números." notes={[{
          tag: 'HERO',
          title: '"Construimos la infraestructura."',
          body: 'Reemplaza "Who are we" por una afirmación de qué hacemos, no de qué somos. La historia de fundación pasa a párrafo de apoyo.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque negro a sangre',
          body: 'Las 80 palabras completas aparecen aquí — es la página donde el manifesto vive en su forma larga. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'VOZ',
          title: 'Tabla usamos / nunca',
          body: 'Bloque visual con las palabras que sí y no usamos. Hace que la política de voz sea pública — útil para clientes y para colaboradores externos.'
        }, {
          tag: 'EQUIPO',
          title: 'Grid 4 columnas',
          body: 'Foto + nombre + rol. Sin bios largas. Squarespace lo permite con bloque image grid estándar.'
        }, {
          tag: 'NÚMEROS',
          title: '"Estaciones por las que hemos pasado"',
          body: 'Reframe de los típicos números de agencia (campañas, marcas, sectores, idiomas) bajo la metáfora de tránsito.'
        }, {
          tag: 'CTA',
          title: '"Let\u2019s connect the dots"',
          body: 'CTA azul a sangre. La invitación más suave del set — para una página que cierra contando quiénes somos, no vendiendo nada.'
        }]} mockup={<AboutPageMockup data-id="element-2200" />} data-id="element-2199" />

          {/* 10.4 — Packs */}
          <PageRedesignBlock number="10.4" title="Packs" url="metroagency.co/packs" intro="Página de packs conectada desde Servicios — cuatro tiers (Micro, Essentials, Professional, Premium) más una sección de add-ons. Precios con IVA incluido para transparencia. Tier 0 deliberadamente limitado como puerta de entrada; cada pack tiene CTA directo de compra y opción de agendar llamada de 15 min." notes={[{
          tag: 'HERO',
          title: 'Cuatro tiers visibles arriba',
          body: 'Strip con los 4 tiers (Micro / Essentials / Professional / Premium) inmediatamente debajo del titular — orientación rápida antes de hacer scroll.'
        }, {
          tag: 'TIER 0',
          title: 'Micro tratado como puerta de entrada',
          body: 'Se presenta solo, con limitaciones explícitas en naranja. Filosofía clara: pack barato para eliminar la objeción de precio, con fricción que empuja al upgrade.'
        }, {
          tag: 'PRECIO',
          title: 'IVA incluido siempre visible',
          body: 'Cada precio lleva la etiqueta "IVA INCL." en mono. Sin asteriscos, sin sorpresas. La pregunta sobre IVA está la primera del FAQ.'
        }, {
          tag: 'COMBOS',
          title: 'Badge "COMBO" con ahorro real',
          body: 'Brand + Web Combo y Full System llevan badge y línea de ahorro. El ahorro está en euros, no en porcentaje vago.'
        }, {
          tag: 'ADD-ONS',
          title: 'Tabla negra con etiqueta "RECURRENTE"',
          body: 'Add-ons mensuales (SEO, social, mantenimiento) van marcados con badge "RECURRENTE" para que el cliente no se sorprenda con cuota.'
        }, {
          tag: 'CTA STICKY',
          title: 'Bloque negro con "Agendar llamada · 15 min"',
          body: 'Sidebar fija en la sección FAQ. Para quien todavía no sabe qué pack comprar — empuja a conversación corta, no a formulario largo.'
        }]} mockup={<PacksPageMockup data-id="element-2202" />} data-id="element-2201" />

          {/* 10.5 — Terms */}
          <PageRedesignBlock number="10.5" title="Términos y condiciones" url="metroagency.co/terminos" intro="Página legal con 15 secciones desde datos de la empresa hasta jurisdicción aplicable. Contenido con placeholders [ Texto pendiente ] mientras se valida con asesoría legal. Layout de índice navegable + contenido en columna estándar — bloque Squarespace de dos columnas con índice anchor." notes={[{
          tag: 'ESTRUCTURA',
          title: '15 secciones numeradas',
          body: 'De "Sobre Metro Agency" a "Jurisdicción". Incluye lo que el usuario pidió: devoluciones (06), garantía post-entrega (07), condiciones de pago (03), cancelaciones (10), más los estándar (RGPD, IP, jurisdicción).'
        }, {
          tag: 'ÍNDICE',
          title: 'TOC fijo a la izquierda',
          body: 'Anchors clicables. Sección actual marcada en azul. Mantiene la página navegable aunque sea larga.'
        }, {
          tag: 'PLACEHOLDERS',
          title: '[ Texto pendiente ] visible',
          body: 'Cada sección que necesita validación de asesoría aparece marcada explícitamente. Las secciones legalmente referenciables (Ley 37/1992, RD 1/2007, RGPD) ya citan la normativa.'
        }, {
          tag: 'ACEPTACIÓN',
          title: 'Bloque azul al cierre',
          body: 'Recuerda que contratar = aceptar, e invita a escribir a legal@metroagency.co antes de firmar si algo no encaja. Tono Metro, no tono abogado.'
        }, {
          tag: 'METADATA',
          title: 'Última actualización + versión',
          body: 'Fecha y versión visibles. Estándar legal cuando los términos cambian con el tiempo.'
        }]} mockup={<TermsPageMockup data-id="element-2204" />} data-id="element-2203" />

          {/* 10.6 — AI Solutions */}
          <PageRedesignBlock number="10.6" title="AI Solutions" url="metroagency.co/ai" intro="Nueva línea de servicio dedicada a IA, conectada desde Packs y desde Servicios. Cinco familias (Chatbot, Workflow, Agents, Consulting, Tools) con precio en formato setup + mensualidad. Hero oscuro para diferenciarla de Packs (claro) — la IA es la cara nueva del estudio, no la repetición de la oferta clásica. Cierra con un bloque de upsells que conecta cada servicio Metro existente con su upgrade IA natural." notes={[{
          tag: 'HERO',
          title: '"Tu negocio, respondiendo a las 2am."',
          body: 'Hero oscuro con copy concreta — habla del problema, no de la tecnología. CTA primario lleva al Audit (€490) porque es la puerta de entrada baja-fricción.'
        }, {
          tag: 'ESTRUCTURA',
          title: '5 familias · precios setup + mensual',
          body: 'Chatbot (3 tiers) · Workflow (3 tiers) · Agents (rango) · Consulting (Audit + Retainer) · Tools (GPTs + Setups). Cada card muestra setup y mensual lado a lado — es la economía real de la IA, no se esconde.'
        }, {
          tag: 'AGENTS',
          title: 'Rango de precio + casos típicos',
          body: 'Para AI Agents no usamos tier porque cada proyecto es custom. Mostramos rango (€3.500–€8.000) y los 4 casos de uso típicos al lado: lead qual, post-venta, reporting, outreach.'
        }, {
          tag: 'UPSELL',
          title: 'Bloque oscuro · conexiones naturales',
          body: 'Tabla "Tienes X · Pregunta · Añades Y" para los upsells que el cliente entiende: Web → Chatbot, Social → DMs bot, Brand → Custom GPT, Cualquiera → Audit. 10% descuento si ya es cliente.'
        }, {
          tag: 'CTA',
          title: '"Empieza por el Audit"',
          body: 'Sticky azul en FAQ. €490, baja fricción, sin compromiso. La invitación es informarse, no contratar el flagship.'
        }, {
          tag: 'CONFIANZA',
          title: 'Datos en UE · RGPD · sin permanencia',
          body: 'Footer de la CTA repite las tres preocupaciones reales del cliente con IA: datos, regulación, lock-in. Sin esto, el resto no cierra.'
        }]} mockup={<AIPageMockup data-id="element-2206" />} data-id="element-2205" />
        </div>

        {/* Sitemap notes */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2207">
          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-2208">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2209">
              NAVEGACIÓN
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-2210">
              Seis entradas. Una salida.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-2211">
              {[['Inicio', 'Punto de entrada. Manifiesto corto + red de servicios + selected work.'], ['Servicios', 'Cuatro estaciones. Listado completo + cómo trabajamos.'], ['Packs', 'Cinco categorías (Web, Brand, Content, Marketing, 360°) + add-ons.'], ['AI', 'Cinco familias de IA · setup + mensual. Auditoría como puerta de entrada.'], ['Nosotros', 'Manifiesto largo + equipo + números.'], ['Contacto', 'Form mínimo. Nombre, email, mensaje. Nada más.'], ['Términos', 'Página legal con 15 secciones. Link en footer.']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-2212">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-2213">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-2214">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-2215">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2216">
              CONSTRUCCIÓN · SQUARESPACE
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-2217">
              Solo bloques estándar.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-2218">
              {[['Hero', 'Section con background color + título + texto + button'], ['Servicios', 'Cards section con 3-4 columnas + icon block'], ['Manifesto', 'Section con background ink/blue + texto a sangre'], ['Trabajos', 'Image gallery grid + link'], ['Equipo', 'People block o image grid 4 cols'], ['Footer', 'Footer estándar Squarespace con 4 columnas']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-2219">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-2220">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-2221">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}